// The job form (finance-real, the user 2026-10-22), a drawer named in the URL (DD-045, DD-051): ?modal=line-job-form makes
// a job on request, active from its start, agreed with no days to start later (the user 2026-10-28), or a draft. A job is
// edited in place in its drawer, which saves as you type, so
// this form only makes one. Opened from a quote it stacks over the quote's drawer or form (the user: "if I create a new job
// it stacks the job modal"), and the job it makes is a draft for that quote. Name, an optional description, its period
// (How it bills, Recurring, One-off or Over a number of posts, and its days in one date-range picker with the Monthly
// shortcut inside: PeriodPicker), an optional list price and discount %, which fill the price when both are typed (off
// the own client), its price and margin %, Done when (when its period ends, or when it is marked done; a
// one-off is always marked, so it has no choice), and Pays all to the team, off the own client, which lifts Setup's
// ceiling so the margin % may be 0.
// What the team can claim under that margin is the server's answer for that price and margin % (TD-102). Claims
// themselves are the job drawer's, but for a team invoice's Add a new job (the user 2026-10-22): it stacks on the team
// invoice's drawer, the client picked first, starts active, and asks for the person's claim on it, made with the job:
// its field is the shared claim amount (ClaimAmountField), the share of the price beside it, the cap and what is free
// under it, or what it waits for until the price and margin % are set.
// From a draft invoice's New job (the user 2026-10-23: "here I can add a new job to the list") it stacks on the invoice's
// drawer, starts active and goes on the draft, then hands back to it.
// From /finance's Jobs (the user 2026-10-24: "an agency job I add that has no client but shows in the global jobs list")
// it picks the client first, as a team invoice's does. Both pickers offer the agency's own client first, as Agency (no
// client), and start on none, since a client's job made on the own client by mistake bills nobody: only a person's New
// job (`startOnAgency`) starts on it, as a one-off dated today: work for the agency logged once it is done. With no own
// client set they leave it out and point to Setup (TD-105). The own client keeps nothing, so its job has no margin %
// field and is sent at 0, as the server stores it.
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { FormField, inputClassName } from "@/components/ui/FormField";
import { MoneyText } from "@/components/ui/MoneyText";
import { OptionDropdown, type DropdownOption } from "@/components/ui/OptionDropdown";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { formatMoney, minorToInput, parseMoneyMinor } from "@/utils/currency";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { ClaimAmountField, claimAmountError, type ClaimJob } from "../components/ClaimForm";
import { MissingLine, Required } from "../components/Required";
import { useSubmitOnce } from "../hooks/useSubmitOnce";
import type { JobDoneWhen, LineJob, LineJobInput, TeamInvoiceDetail } from "../types";
import { closeUnlessPending } from "../utils/mutations";
import { AGENCY, AGENCY_CHOICE } from "../utils/names";
import { useLineJobFormDrawer } from "./hooks/useLineJobFormDrawer";
import { DISCOUNT_ERROR, discountedMinor, discountOf, DONE_WHEN, doneWhenHint, LIST_PRICE_ERROR, LIST_PRICE_HINT, marginHint, PAYS_ALL_HINT, priceLabel } from "./LineJobDrawer";
import { newPeriod, PeriodPicker, periodDays, periodError, rhythmFrom, type PeriodValue } from "./PeriodPicker";

// `contract` names the user's job now (the entityConfig shadow).
const JOB = ENTITY_CONFIG.contract;
const jobWord = JOB.label.toLowerCase();
const quoteWord = ENTITY_CONFIG.quote.label.toLowerCase();

type StartsAs = LineJobInput["status"];

/** How a job made here starts, in the form's one picker component (DD-056), as How it bills is. */
const STARTS_AS: DropdownOption<StartsAs>[] = [
  { value: "active", label: "Active, on request" },
  { value: "agreed", label: "Agreed, starts later" },
  { value: "draft", label: "Draft" },
];

/** What a job made agreed or a draft waits for, under Starts as. */
const STARTS_AS_HINT: Partial<Record<StartsAs, string>> = {
  agreed: `The ${ENTITY_CONFIG.client.label.toLowerCase()} said yes: it waits on the ${JOB.plural} tab with no days, and its status starts it.`,
  draft: `A draft waits on the ${JOB.plural} tab, for a ${quoteWord} or to start later.`,
};

/** What the cap waits for, before the price and margin % are set. */
const WAITS_FOR_PRICE = "What is free to claim shows once the price and margin % are set: the price less the margin, to the cent.";

/** A client a new job can go on, in the form's client picker. */
export type ClientChoice = TeamInvoiceDetail["clients"][number];

/** Setup's Agency tab with the own client's drawer open. */
export const SET_OWN_CLIENT = "/setup?modal=own-client";

/** The picker's order: the agency's own first, as Agency (no client), then every other client by name. */
const choiceOptions = (clients: ClientChoice[]): DropdownOption<string>[] =>
  [...clients.filter((c) => c.ownClient), ...clients.filter((c) => !c.ownClient).sort((a, b) => a.name.localeCompare(b.name))].map((c) => ({
    value: c.id,
    label: c.ownClient ? AGENCY_CHOICE : c.name,
  }));

const wholeOf = (text: string): number | null => {
  const n = Number(text);
  return text.trim() !== "" && Number.isInteger(n) ? n : null;
};

interface FormProps {
  clientId: string;
  /** A new job for a quote: a draft, on this quote when it has one already (null for one still being written). It stacks
   * on the quote's drawer or form, with no second backdrop. */
  forQuote?: { quoteId: string | null; title: string };
  /** The agency's own client (TD-105): it keeps nothing, so no least margin. */
  own: boolean;
  today: string;
  onSaved: (job: LineJob) => void;
  onClose: () => void;
  /** A new job for a team invoice: on the client picked here, active, with this person's claim. It closes once made. */
  forTeam?: { invoiceId: string; personName: string; title: string; clients: { id: string; name: string; ownClient: boolean }[] };
  /** A new job for a draft invoice: active, on it. It closes once made, back to the invoice. */
  forInvoice?: { invoiceId: string; title: string };
  /** A new job from /finance's Jobs: on the client picked here, made as on its page and handed to `onSaved`. */
  forAgency?: { clients: ClientChoice[] };
  /** A person's New job, extra work for the agency, usually done already: the picker starts on the agency's own client,
   * the period on a one-off dated today. */
  startOnAgency?: boolean;
}

export function LineJobFormDrawer({ clientId: givenClient, forQuote, own: givenOwn, today, onSaved, onClose, forTeam, forInvoice, forAgency, startOnAgency = false }: FormProps) {
  const send = useSubmitOnce();
  const choices = forTeam?.clients ?? forAgency?.clients;
  const [pickedClient, setPickedClient] = useState(() => (startOnAgency ? (choices?.find((c) => c.ownClient)?.id ?? "") : ""));
  const [claimMinor, setClaimMinor] = useState<number | null>(null);
  const picked = choices?.find((c) => c.id === pickedClient);
  const clientId = choices ? pickedClient : givenClient;
  const own = choices ? (picked?.ownClient ?? false) : givenOwn;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [period, setPeriod] = useState<PeriodValue>(() => (startOnAgency ? { ...newPeriod(today), mode: "one_off", endsOn: "" } : newPeriod(today)));
  const [price, setPrice] = useState("");
  const [listPrice, setListPrice] = useState("");
  const [discount, setDiscount] = useState("");
  const [margin, setMargin] = useState("30");
  const [paysAllBox, setPaysAll] = useState(false);
  const [startAs, setStartAs] = useState<StartsAs>("active");
  const [doneWhen, setDoneWhen] = useState<JobDoneWhen>("period_end");
  const [touched, setTouched] = useState(false);
  // A job not started (a draft, an agreed one, a quote's) has no start day: setting it active gives it one.
  const started = !forQuote && (forTeam !== undefined || forInvoice !== undefined || startAs === "active");
  const rhythm = rhythmFrom(period);
  const priceMinor = parseMoneyMinor(price);
  const validPrice = priceMinor !== null && priceMinor > 0 ? priceMinor : null;
  const listMinor = parseMoneyMinor(listPrice);
  // The own client bills nobody (TD-105): it has no list price, and none is sent.
  const validList = !own && listMinor !== null && listMinor > 0 ? listMinor : null;
  // Both typed, the price is the list price less the discount; it stays editable.
  const fillPrice = (listText: string, discountText: string) => {
    const list = parseMoneyMinor(listText);
    const pct = discountOf(discountText);
    if (list !== null && list > 0 && pct !== null) setPrice(minorToInput(discountedMinor(list, pct)));
  };
  const typedMargin = own ? 0 : wholeOf(margin);
  // The own client has no ceiling anyway (TD-105): the box is hidden there and never sent.
  const paysAll = paysAllBox && !own;
  const { least, marginPct, cost, costCurrent, create, createForTeam, createForInvoice } = useLineJobFormDrawer(clientId, own, validPrice, typedMargin, paysAll);
  const pending = create.isPending || createForTeam.isPending || createForInvoice.isPending;
  // The claim's job, once the server has its cap for the price and margin % typed now (TD-102): nobody claims on it yet.
  // A cap still for the last price or margin % (resting, or kept on screen while the new one loads) is no job yet, so
  // the claim is never checked against it.
  const claimJob: ClaimJob | null =
    forTeam && cost.data && costCurrent && validPrice !== null && marginPct !== null
      ? { id: "new", label: name.trim(), priceMinor: validPrice, marginPct, capMinor: cost.data.capMinor, freeMinor: cost.data.freeMinor }
      : null;
  const errors = {
    client: choices && !picked ? "Pick the client it is for" : undefined,
    claim: !forTeam
      ? undefined
      : claimJob
        ? claimAmountError(claimMinor, claimJob)
        : claimMinor === null || claimMinor <= 0
          ? "An amount above zero, at most what the team can claim"
          : validPrice !== null && marginPct !== null
            ? "Wait for what is free to claim at this price and margin %"
            : undefined,
    name: name.trim() === "" ? "A name is required" : undefined,
    price: validPrice === null ? "A price above zero, like 250,00" : undefined,
    listPrice: !own && listPrice.trim() !== "" && validList === null ? LIST_PRICE_ERROR : undefined,
    discount: !own && discount.trim() !== "" && discountOf(discount) === null ? DISCOUNT_ERROR : undefined,
    margin:
      marginPct !== null
        ? undefined
        : typedMargin !== null && typedMargin < least && typedMargin >= 0
          ? `At least ${least}%: the team claims at most ${100 - least}% of a ${jobWord} (Setup)`
          : `A whole number from ${least} to 100`,
    period: periodError(period, started),
  };
  const ok = Object.values(errors).every((e) => e === undefined);
  const shown = (error: string | undefined) => (touched ? error : undefined);

  const submit = () => {
    setTouched(true);
    if (!ok || rhythm === null || validPrice === null || marginPct === null) return;
    const input: LineJobInput = {
      name: name.trim(),
      description: description.trim() || null,
      rhythm,
      priceMinor: validPrice,
      marginPct,
      paysAllToTeam: paysAll,
      status: forQuote ? "draft" : forTeam || forInvoice ? "active" : startAs,
      ...periodDays(period, started),
      quoteId: forQuote?.quoteId ?? null,
      doneWhen: period.mode === "one_off" ? "marked" : doneWhen,
      ...(validList !== null ? { listPriceMinor: validList } : {}),
    };
    if (forTeam) {
      if (claimMinor === null) return;
      send(() => createForTeam.mutateAsync({ invoiceId: forTeam.invoiceId, input: { clientId, job: input, amountMinor: claimMinor } }), onClose);
      return;
    }
    if (forInvoice) {
      send(() => createForInvoice.mutateAsync({ invoiceId: forInvoice.invoiceId, input }), onClose);
      return;
    }
    send(() => create.mutateAsync(input), onSaved);
  };
  const title = forQuote ? `${JOB.createLabel} · ${forQuote.title}` : forTeam ? `${JOB.createLabel} · ${forTeam.title}` : forInvoice ? `${JOB.createLabel} · ${forInvoice.title}` : JOB.createLabel;
  // DD-056: a button that records money names the amount once it is known ("Start it with the claim · 150,00 €").
  const action = forQuote
    ? `Add to the ${quoteWord}`
    : forInvoice
      ? `Add to the ${ENTITY_CONFIG.invoice.label.toLowerCase()}`
      : forTeam
      ? `Start it with the claim${claimMinor !== null && claimMinor > 0 ? ` · ${formatMoney(claimMinor, "EUR")}` : ""}`
      : startAs === "active"
        ? `Start the ${jobWord}`
        : startAs === "agreed"
          ? "Make it agreed"
          : "Make the draft";
  // What the button waits for, said before it is pressed (DD-056).
  const missing = [
    choices && !picked ? `the ${ENTITY_CONFIG.client.label.toLowerCase()}` : null,
    name.trim() === "" ? "a name" : null,
    validPrice === null ? "a price" : null,
    errors.period ? "its period" : null,
    forTeam && (claimMinor === null || claimMinor <= 0) ? `${forTeam.personName}'s claim` : null,
  ].filter((m): m is string => m !== null);

  return (
    <FinanceDrawer
      title={title}
      stacked={forQuote !== undefined || forTeam !== undefined || forInvoice !== undefined}
      onClose={closeUnlessPending(pending, onClose)}
      // No client picked, nothing is made: MissingLine says the client is what it waits for.
      footer={
        <Button disabled={pending || (choices !== undefined && !picked)} isLoading={pending} onClick={submit}>
          {action}
        </Button>
      }
    >
      <div className="space-y-4">
        {choices && (
          <FormField
            label={<Required>{ENTITY_CONFIG.client.label}</Required>}
            error={shown(errors.client)}
            hint={
              choices.some((c) => c.ownClient) ? undefined : (
                <>
                  {`No ${ENTITY_CONFIG.client.label.toLowerCase()} is the agency's own yet: name it in `}
                  <Link to={SET_OWN_CLIENT} className="text-blue-600 hover:text-blue-700">
                    {ENTITY_CONFIG.setup.label}
                  </Link>
                  {` to add ${AGENCY.toLowerCase()} ${JOB.plural.toLowerCase()}.`}
                </>
              )
            }
          >
            <OptionDropdown
              variant="input"
              ariaLabel={ENTITY_CONFIG.client.label}
              value={picked ? picked.id : null}
              placeholder={`Pick the ${ENTITY_CONFIG.client.label.toLowerCase()} first`}
              options={choiceOptions(choices)}
              onChange={setPickedClient}
            />
          </FormField>
        )}
        <FormField label={<Required>Name</Required>} error={shown(errors.name)}>
          <input aria-label="Name" aria-required="true" className={inputClassName} value={name} maxLength={120} placeholder="Shooting" onChange={(e) => setName(e.target.value)} />
        </FormField>
        <FormField label="Description" hint={`Optional: what this ${jobWord} covers, this period.`}>
          <textarea aria-label="Description" rows={2} className={inputClassName} value={description} maxLength={500} placeholder="Two shoots, the autumn menu" onChange={(e) => setDescription(e.target.value)} />
        </FormField>
        {!forQuote && !forTeam && !forInvoice && (
          <FormField label="Starts as" hint={STARTS_AS_HINT[startAs]}>
            <OptionDropdown variant="input" ariaLabel="Starts as" value={startAs} options={STARTS_AS} onChange={setStartAs} />
          </FormField>
        )}
        <PeriodPicker value={period} onChange={setPeriod} dated={started} error={shown(errors.period)} />
        {!own && (
          <div className="grid grid-cols-2 gap-3">
            <FormField label="List price" hint={LIST_PRICE_HINT} error={shown(errors.listPrice)}>
              <input
                aria-label="List price"
                inputMode="decimal"
                className={inputClassName}
                value={listPrice}
                placeholder="800,00"
                onChange={(e) => {
                  setListPrice(e.target.value);
                  fillPrice(e.target.value, discount);
                }}
              />
            </FormField>
            <FormField label="Discount %" error={shown(errors.discount)}>
              <input
                type="number"
                min={0}
                max={100}
                step="any"
                aria-label="Discount %"
                className={inputClassName}
                value={discount}
                onChange={(e) => {
                  setDiscount(e.target.value);
                  fillPrice(listPrice, e.target.value);
                }}
              />
            </FormField>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <FormField label={<Required>{priceLabel(own, period.mode === "one_off")}</Required>} error={shown(errors.price)}>
            <input aria-label="Price" aria-required="true" inputMode="decimal" className={inputClassName} value={price} placeholder="250,00" onChange={(e) => setPrice(e.target.value)} />
          </FormField>
          {!own && (
            <FormField label={<Required>Margin %</Required>} hint={marginHint(least)} error={shown(errors.margin)}>
              <input type="number" aria-required="true" min={least} max={100} step={1} aria-label="Margin %" className={inputClassName} value={margin} onChange={(e) => setMargin(e.target.value)} />
            </FormField>
          )}
        </div>
        {period.mode !== "one_off" && (
          <FormField label="Done when" hint={doneWhenHint(doneWhen)}>
            <OptionDropdown variant="input" ariaLabel="Done when" value={doneWhen} options={DONE_WHEN} onChange={setDoneWhen} />
          </FormField>
        )}
        {!own && (
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" className="mt-0.5" checked={paysAllBox} onChange={(e) => setPaysAll(e.target.checked)} />
            <span>
              Pays all to the team
              <span className="block text-xs text-gray-400">{PAYS_ALL_HINT}</span>
            </span>
          </label>
        )}
        {/* A team invoice's new job says it with the claim's own field (ClaimAmountField), under it. */}
        {!forTeam && (
          <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
            {cost.data ? (
              <span>
                {"Free to claim: "}
                <MoneyText amountMinor={cost.data.freeMinor} strong />
                {" of its "}
                <MoneyText amountMinor={cost.data.capMinor} />
                {` cap. ${ENTITY_CONFIG.claim.plural} are recorded on the ${jobWord}, once it is made.`}
              </span>
            ) : (
              WAITS_FOR_PRICE
            )}
          </div>
        )}
        {forInvoice && createForInvoice.error && <p className="text-xs text-red-600">{createForInvoice.error.message}</p>}
        {forTeam && (
          <ClaimAmountField
            label={<Required>{`${forTeam.personName}'s claim`}</Required>}
            ariaLabel={`${forTeam.personName}'s claim`}
            valueMinor={claimMinor}
            onChange={setClaimMinor}
            job={claimJob}
            waiting={WAITS_FOR_PRICE}
            error={createForTeam.error?.message ?? (touched ? errors.claim : undefined)}
          />
        )}
        {forInvoice && <p className="text-xs text-gray-500">{`It starts active and goes on the draft ${ENTITY_CONFIG.invoice.label.toLowerCase()}, invoiced with it.`}</p>}
        {forQuote && <p className="text-xs text-gray-500">{`Once the ${quoteWord} is accepted it is agreed, and its own status starts it.`}</p>}
        <MissingLine button={action.split(" · ")[0]} missing={missing} />
      </div>
    </FinanceDrawer>
  );
}
